/**
 * App shell — left sidebar with workspace switcher + nav, content
 * region for the routed page.
 *
 * Navigation (src/app/lib/navigation.ts): nine primary sections in the
 * sidebar; each section's pages are tabs (<SectionTabs>) above the page.
 * Account, preferences and resources sit in a compact footer menu next to
 * the plan/usage block. Every existing route keeps its URL.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Settings as SettingsIcon, ChevronDown, CircleUser, Kanban, Sparkles } from "lucide-react";
import { useWorkspace } from "../hooks/useWorkspace";
import { TaskChecklist } from "./TaskChecklist";
import { PageContextSync } from "./PageContextSync";
import { LiveCanvasPanel } from "./LiveCanvasPanel";
import { AccountAccess } from "./AccountAccess";
import { PlanRequiredBanner } from "./PlanRequiredBanner";
import { SectionTabs } from "./SectionTabs";
import {
  ClientContextBanner,
  BrowserBusStatusPill,
  AttentionBell,
  CommandPalette,
  LiveAISessionRail,
} from "../../framework/react/index.js";
import { getCsrfHeader } from "../lib/csrf";
import { US_STATE_NAMES } from "../../lib/us-states";
import { NotificationInboxLink } from "./NotificationInboxLink";
import { FOOTER_NAV, NAV_SECTIONS, resolveFooterNav, resolveNav, workspacePath } from "../lib/navigation";

const FOOTER_MENU_KEY = "cmcp:sidebar-more-open";
function readFooterOpen(): boolean {
  try { return window.localStorage.getItem(FOOTER_MENU_KEY) === "1"; } catch { return false; }
}
function writeFooterOpen(open: boolean) {
  try { window.localStorage.setItem(FOOTER_MENU_KEY, open ? "1" : "0"); } catch { /* storage unavailable */ }
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { current, workspaces, setCurrent, isLoading, error: workspaceError, retry: retryWorkspaces } = useWorkspace();
  const location = useLocation();
  const stateView = new URLSearchParams(location.search).get("jurisdiction");

  // Mobile drawer state. Sidebar is fixed-overlay below md breakpoint;
  // hamburger in the mobile top bar toggles it. Auto-closes on route
  // change so tapping a nav item dismisses the drawer.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  // Track viewport so the inline transform doesn't fight the md:relative
  // static layout. Below md → drawer; at md+ → always visible. Both
  // matchMedia 'change' AND window 'resize' are wired because some
  // environments (notably DevTools-style preview resizers) update the
  // viewport without firing matchMedia change events.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    window.addEventListener("resize", handler);
    return () => {
      mq.removeEventListener("change", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  const activeNav = resolveNav(location.pathname, location.search);
  const footerActive = resolveFooterNav(location.pathname, location.search);
  // Footer menu stays open when the current page lives in it.
  const [footerOpen, setFooterOpen] = useState(() => readFooterOpen() || Boolean(footerActive));
  useEffect(() => {
    if (footerActive) setFooterOpen(true);
  }, [footerActive]);

  return (
    <div className="cmcp-app min-h-screen flex">
      {/* Mobile top bar — only visible below md. Carries the hamburger,
          app brand, and the attention bell so users can reach everything
          one tap away even when the sidebar is collapsed. */}
      <header className="cmcp-mobile-bar md:hidden fixed top-0 inset-x-0 z-30 px-3 py-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="cmcp-mobile-menu p-1.5 -ml-1 rounded-md"
          aria-label="Open navigation"
          aria-expanded={mobileNavOpen}
          aria-controls="workspace-navigation"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <Link to="/dashboard" className="cmcp-brand flex-1 min-w-0">
          CongressMCP
        </Link>
        <NotificationInboxLink workspaceId={current?.id ?? null} />
        <AttentionBell workspaceId={current?.id ?? null} csrfHeader={getCsrfHeader} />
      </header>

      {/* Backdrop — closes the drawer on tap. Only renders while the
          mobile drawer is open. */}
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/30"
        />
      )}

      {/* Sidebar — static aside on md+, fixed-overlay drawer below md.
          Transform handled via inline style (gated by the isMobile media-
          query check) because Tailwind's translate utilities weren't
          reliably applying the transform property in this pipeline
          configuration. The isMobile gate keeps md+ rendering exactly as
          before. */}
      <aside
        id="workspace-navigation"
        inert={isMobile && !mobileNavOpen}
        aria-hidden={isMobile && !mobileNavOpen ? true : undefined}
        className={
          "cmcp-sidebar px-3 pt-5 pb-16 flex flex-col gap-3 " +
          "md:relative md:w-60 md:shrink-0 " +
          "fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto transition-transform duration-200"
        }
        style={
          isMobile
            ? {
                transform: mobileNavOpen ? "translateX(0)" : "translateX(-100%)",
              }
            : undefined
        }
      >
        <div className="cmcp-sidebar-brand px-2 mb-2 flex items-center gap-2">
          <Link
            to="/dashboard"
            className="cmcp-brand flex-1 min-w-0"
          >
            <span className="cmcp-brand-mark" aria-hidden="true">cm</span>Congress<span className="cmcp-brand-light">MCP</span>
          </Link>
          {/* Attention queue — bell icon with count badge. Click opens
              a dropdown of pending decisions (champion gaps, signal
              reviews, conflicts, cooling relationships) with one-click
              resolve / defer / dismiss actions. */}
          <NotificationInboxLink workspaceId={current?.id ?? null} />
          <AttentionBell workspaceId={current?.id ?? null} csrfHeader={getCsrfHeader} />
        </div>

        {/* Search hint — clicking dispatches a synthetic ⌘K via the
            same keyboard handler the CommandPalette listens for. */}
        <button
          type="button"
          onClick={() => {
            const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
            document.dispatchEvent(ev);
          }}
          className="cmcp-nav-search mx-2 flex items-center gap-2 px-3 py-2 rounded-md mb-1"
          title="Open command palette"
        >
          <Search size={13} strokeWidth={1.75} aria-hidden className="text-stone-400" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="cmcp-search-shortcut">
            ⌘K
          </kbd>
        </button>

        {/* Workspace switcher */}
        <div className="px-2">
          <div className="cmcp-nav-label mb-2 flex items-center justify-between">
            <label htmlFor="workspace-select">Workspace</label>
            {current?.owner_type === "organization" && (
              <Link
                to={`/org-branding/${current.owner_id}`}
                className="text-stone-400 hover:text-stone-700 normal-case font-normal tracking-normal"
                title="Org branding settings"
              >
                <SettingsIcon size={12} strokeWidth={1.75} aria-hidden />
              </Link>
            )}
          </div>
          <select
            id="workspace-select"
            className="w-full text-sm border border-stone-200 rounded-md px-2 py-1.5 bg-white"
            value={current?.id ?? ""}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={isLoading || workspaces.length === 0}
          >
            {isLoading && <option>Loading…</option>}
            {!isLoading && workspaces.length === 0 && <option>{workspaceError ? "Workspaces unavailable" : "No workspaces"}</option>}
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
          {workspaceError && <p role="alert" className="cmcp-nav-error">Your workspaces could not be loaded. <button type="button" onClick={retryWorkspaces}>Retry</button></p>}
        </div>


        {/* Nine primary sections. A section is active when one of its tabs
            owns the current URL (see resolveNav). */}
        <nav aria-label="Workspace sections" className="cmcp-primary-nav flex flex-col gap-0.5 mt-2">
          {NAV_SECTIONS.map(({ id, Icon, label, tabs }) => {
            const active = !footerActive && activeNav?.section.id === id;
            return (
              <Link
                key={id}
                to={workspacePath(tabs[0].to, current?.id)}
                aria-current={active ? "page" : undefined}
                className={`cmcp-nav-link cmcp-nav-section-link ${active ? "is-active" : ""}`}
              >
                <Icon size={16} strokeWidth={1.75} aria-hidden />
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="cmcp-sidebar-account mt-auto px-2 pt-4">
          {/* Account, preferences and resources: one compact, remembered
              disclosure instead of three extra nav groups. */}
          <details
            className="cmcp-nav-more -mx-2 mb-3"
            open={footerOpen}
            onToggle={(event) => {
              const open = (event.currentTarget as HTMLDetailsElement).open;
              setFooterOpen(open);
              writeFooterOpen(open);
            }}
          >
            <summary>
              <CircleUser size={16} strokeWidth={1.75} aria-hidden />
              Account & resources
              <ChevronDown size={14} aria-hidden className="cmcp-nav-more-chevron" />
            </summary>
            <nav aria-label="Account and resources" className="flex flex-col gap-0.5 mt-1">
              {FOOTER_NAV.map(({ id, label, to, external }) => {
                const active = footerActive?.id === id;
                return external
                  ? <a key={id} href={workspacePath(to, current?.id)} className="cmcp-nav-link">{label}</a>
                  : (
                    <Link
                      key={id}
                      to={workspacePath(to, current?.id)}
                      aria-current={active ? "page" : undefined}
                      className={`cmcp-nav-link ${active ? "is-active" : ""}`}
                    >
                      {label}
                    </Link>
                  );
              })}
            </nav>
          </details>
          <AccountAccess />
          <div className="cmcp-bus-status"><span>Workspace updates</span><BrowserBusStatusPill /></div>
        </div>
      </aside>

      {/* Content. The pt-12 below md pushes content past the fixed
          mobile top bar; md+ removes the offset since the top bar isn't
          rendered. */}
      <main className="min-w-0 flex-1 overflow-auto flex flex-col pt-12 md:pt-0">
        <div className="cmcp-workspace-bar">
          <div>
            <span className="cmcp-workspace-eyebrow">{stateView ? US_STATE_NAMES[stateView] ?? stateView : "Federal + 50 states + D.C."}</span>
            <strong>{current?.name ?? (isLoading ? "Loading workspace…" : "National research")}</strong>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={workspacePath("/whip-boards", current?.id)} className="cmcp-research-link">
              <Kanban size={15} aria-hidden /> Whip boards
            </Link>
            <Link to={workspacePath("/discover", current?.id)} className="cmcp-research-link">
              <Search size={16} aria-hidden /> Find legislation
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("cmcp:prefill-chat", { detail: { workspaceId: current?.id, prompt: "" } }))}
              className="cmcp-btn-primary cmcp-btn-sm"
              title="Open AI assistant"
            >
              <Sparkles size={14} aria-hidden /> Ask AI
            </button>
          </div>
        </div>
        {/* Plan gate — shows only after a workspace write came back 402 plan_required */}
        <PlanRequiredBanner />
        {/* AI-driven context banner — pinned top of content when set_client_context fires */}
        <ClientContextBanner />
        {/* Section tabs: the pages inside the active sidebar section. */}
        <SectionTabs />
        <div className="flex-1 overflow-auto">{children}</div>
      </main>

      {/* External Claude/Cursor/Codex work is visible globally, including on
          mobile. The rail also lets the human pause visual browser control
          without stopping safe data refreshes. */}
      <LiveAISessionRail workspaceId={current?.id ?? null} />

      {/* Right-side task checklist — auto-shows when the AI proposes a
          plan, ticks off as steps execute. */}
      <TaskChecklist />

      {/* Live Canvas — docked panel that mirrors the AI's most recent
          render_inline_panel push. The user's AI client is the control
          surface; this panel is the display. Auto-shows on fresh content,
          collapsible to a tab on the right edge. */}
      <LiveCanvasPanel workspaceId={current?.id ?? null} />

      {/* Universal ⌘K command palette. Listens at document level for
          ⌘K (mac) / Ctrl-K (win/linux) and opens a centered modal with
          fuzzy search across bills, members, contacts, orgs, and
          issues — all scoped to the current workspace. */}
      <CommandPalette workspaceId={current?.id ?? null} />

      {/* Server-side sync of window.__pageContext so the user's MCP
          client (get_active_page_context / get_user_screenshot) knows what
          the user is looking at between turns (FB-MOX8LBC8).
          Polls every 3s; upserts only when the snapshot changes. */}
      <PageContextSync workspaceId={current?.id ?? null} />
    </div>
  );
}
